import { cache, getStoredTheme, setTheme, type Theme } from "@serophin/browser";
import { putSetting, SettingKey } from "@serophin/nottldr-shared";
import { useSettings } from "@serophin/nottldr-shared/hooks";
import { COMMON_CACHE_KEYS } from "@serophin/shared";
import { useEffect } from "preact/hooks";
import { useIsSignedIn } from "./session";

/** Where the first site kept the settings, `{ value: { theme, ... } }`. */
const FIRST_SITE_SETTINGS_KEY = "cache:settings";

const THEMES: readonly string[] = ["system", "light", "dark"];

const isTheme = (value: unknown): value is Theme => typeof value === "string" && THEMES.includes(value);

/**
 * One-time carry-over of the theme a returning visitor chose on the first site: copies it to the
 * framework's theme key (the one the inline script in the document head reads) and drops the old entry.
 */
export const migrateLegacyTheme = (): void => {
  try {
    const raw = localStorage.getItem(FIRST_SITE_SETTINGS_KEY);
    if (raw === null) {
      return;
    }
    localStorage.removeItem(FIRST_SITE_SETTINGS_KEY);
    const theme: unknown = JSON.parse(raw)?.value?.theme;
    if (cache.get(COMMON_CACHE_KEYS.theme) === null && isTheme(theme)) {
      cache.set(COMMON_CACHE_KEYS.theme, theme);
    }
  } catch (error) {
    console.error("Failed to migrate the old theme", error);
  }
};

/** Switches the theme now and saves it as the user's `theme` setting (to the API when signed in, always to the cache). */
export const changeTheme = (theme: Theme): void => {
  setTheme(theme);
  putSetting(SettingKey.THEME, theme).catch((error: unknown) => {
    console.error("Failed to save the theme", error);
  });
};

/**
 * Keeps this browser's theme in step with the signed-in user's `theme` setting, so a choice made on another
 * device arrives here. Signed out, the browser's own choice stands.
 */
export const useThemeSync = (): void => {
  const signedIn = useIsSignedIn();
  const { settings, isLoading } = useSettings();
  const wanted = settings.theme;

  useEffect(() => {
    if (signedIn && !isLoading && wanted && wanted !== getStoredTheme()) {
      setTheme(wanted);
    }
  }, [signedIn, isLoading, wanted]);
};
